<template>
  <div class="overflow-x-auto rounded-lg shadow-sm border border-gray-200">
    <table class="min-w-full divide-y divide-gray-200 bg-white/50">
      <thead class="bg-gray-100/80">
        <tr>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">#</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Name</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Location</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Size (acres)</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Rent Time(seasons)</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Created By</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Owner</th>
          <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200">
        <tr v-for="farm in farms" :key="farm.id" class="hover:bg-gray-50/70 transition-colors">
          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ farm.number }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ farm.name }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{{ farm.location }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{{ farm.size_with_unit || farm.size }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ farm.rented_seasons || 'None' }}</td>
          <td class="px-6 py-4 whitespace-nowrap">
            <span :class="getStatusClass(farm.status)" class="px-2 py-1 text-xs font-medium rounded-full">
              {{ getStatusLabel(farm.status) }}
            </span>
          </td>

          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ farm.created_by_name }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ farm.owner_name }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
            <button
              @click="$emit('edit', farm)"
              class="text-blue-600 hover:text-blue-900 mr-3 transition-colors"
              title="Edit Farm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              @click="$emit('delete', farm)"
              class="text-red-600 hover:text-red-900 transition-colors"
              title="Delete Farm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </td>
        </tr>
        <tr v-if="farms.length === 0">
          <td colspan="6" class="px-6 py-4 text-center text-sm text-gray-500">
            No farms found matching your criteria.
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import type { Farm } from '@/types/Farm'

defineProps<{
  farms: (Farm & { number: number })[]
}>()

defineEmits<{
  (e: 'edit', farm: Farm): void
  (e: 'delete', farm: Farm): void
}>()

const getStatusLabel = (status: string) => {
  const statusOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'RENTED_FROM', label: 'Rented From' },
    { value: 'RENTED_TO', label: 'Rented To' },
    { value: 'SOLD', label: 'Sold' },
  ]
  return statusOptions.find(opt => opt.value === status)?.label || status
}

const getStatusClass = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-800'
    case 'RENTED_FROM':
      return 'bg-blue-100 text-blue-800'
    case 'RENTED_TO':
      return 'bg-purple-100 text-purple-800'
    case 'SOLD':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}
</script>
